import React, { useEffect } from 'react';
import { RegisterOptions, useFormContext } from 'react-hook-form';

import { AIHintProps } from 'components/AIHint';
import InputPreviousValue from 'components/InputPreviousValue';
import { Typography } from 'components/typography';
import {
  ExtendedFormContextType,
  useExtendedFormContext,
} from 'contexts/extendedFormContext';
import { useFieldAttributes } from 'hooks/useLoadContext';
import {
  FieldAttributes,
  getFieldAttribute,
  initFieldAttributes,
} from 'types/LoadAttributes';
import { Maybe } from 'types/UtilityTypes';
import { cn } from 'utils/shadcn';

import FormInputWrapper from './FormInputWrapper';
import { Input } from './Input';

export enum InputValue {
  PHONE_NUMBER = 'PHONE_NUMBER',
}

type RHFTextInputProps = AIHintProps & {
  name: string;
  label: string;
  description?: string;
  required?: boolean;
  defaultValue?: string;
  placeholder?: string;
  options?: RegisterOptions<any>;
  inputValue?: InputValue;
  step?: number; // Step size (e.g. 0.01) for inputs where inputType === 'number'
  inputType?:
    | 'button'
    | 'checkbox'
    | 'color'
    | 'datetime-local'
    | 'email'
    | 'file'
    | 'hidden'
    | 'image'
    | 'month'
    | 'number'
    | 'password'
    | 'radio'
    | 'range'
    | 'reset'
    | 'search'
    | 'submit'
    | 'tel'
    | 'text'
    | 'time'
    | 'url'
    | 'week';
  prevValue?: string;
  requiredIcon?: boolean;
  highlightError?: boolean;
  labelClassName?: string;
  inputClassName?: string;
  maxLength?: number;
  inlineDimensionUnit?: string;
  labelAdornment?: React.ReactNode;
  inputEndAdornment?: React.ReactNode;
  'data-testid'?: string;
};

export function RHFTextInput({
  name,
  label,
  required,
  defaultValue,
  placeholder,
  options,
  inputValue,
  inputType = 'text',
  prevValue,
  step = 0.01,
  highlightError = false,
  labelClassName = '',
  inputClassName = '',
  maxLength,
  inlineDimensionUnit,
  labelAdornment,
  inputEndAdornment,
  ...props
}: RHFTextInputProps) {
  required = required || hasRequiredValidation(options);
  const allFieldAttrs = useFieldAttributes();
  const { register, setValue, getValues, resetField } = useFormContext();

  let extendedFormContext: Maybe<ExtendedFormContextType> = null;

  try {
    extendedFormContext = useExtendedFormContext();
  } catch {
    // no extended form context available
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const oldValue = getValues(name);
    let newValue = event.target.value;

    if (maxLength && newValue.length > maxLength) {
      newValue = newValue.slice(0, maxLength);
      event.target.value = newValue;
    }

    if (inputValue === InputValue.PHONE_NUMBER) {
      newValue = FormatPhoneNumber(newValue);
    }

    setValue(name, newValue);

    // track field change if callback exists
    if (extendedFormContext?.onFieldChange) {
      extendedFormContext.onFieldChange(name, oldValue, newValue);
    }
  };

  // Scientific notation (using 'e' or 'E') should not be allowed in number inputs
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (inputType === 'number' && (event.key === 'e' || event.key === 'E')) {
      event.preventDefault();
    }
  };

  const handleWheel = (event: React.WheelEvent<HTMLInputElement>) => {
    if (inputType === 'number') {
      event.currentTarget.blur();
    }
  };

  const formattedPhoneValue =
    inputValue === InputValue.PHONE_NUMBER
      ? FormatPhoneNumber(defaultValue || getValues(name))
      : defaultValue || getValues(name);

  const thisFieldAttr: FieldAttributes = (() => {
    // Normalize nested ValueUnit paths and array index paths so backend attrs can
    // drive read-only behavior without section-specific FE logic.
    const normalizedName = normalizeFieldAttributePath(name);

    const exactMatch = getFieldAttribute(allFieldAttrs, normalizedName);
    if (exactMatch) {
      return exactMatch;
    }

    // Fallback: progressively trim leaf segments to allow container-level attrs
    // (e.g. pickup.additionalReferences) to apply to nested inputs.
    let parentPath = normalizedName;
    for (;;) {
      const lastDot = parentPath.lastIndexOf('.');
      if (lastDot < 0) {
        break;
      }
      parentPath = parentPath.slice(0, lastDot);
      const parentMatch = getFieldAttribute(allFieldAttrs, parentPath);
      if (parentMatch) {
        return parentMatch;
      }
    }

    return initFieldAttributes;
  })();

  // For read-only phone number fields, format default value so it passes validation.
  useEffect(() => {
    if (
      inputValue === InputValue.PHONE_NUMBER &&
      thisFieldAttr.isReadOnly &&
      formattedPhoneValue
    ) {
      resetField(name, { defaultValue: formattedPhoneValue });
    }
  }, [name]);

  return thisFieldAttr.isNotSupported ? null : (
    <FormInputWrapper
      name={name}
      label={label}
      readOnly={thisFieldAttr.isReadOnly || props.readOnly}
      required={required}
      className={labelClassName}
      labelAdornment={labelAdornment}
      {...props}
    >
      <div className='relative'>
        <Input
          readOnly={thisFieldAttr.isReadOnly || props.readOnly}
          placeholder={placeholder}
          type={inputType}
          min={inputType === 'number' ? 0 : undefined} // Handle `min` conditionally for numbers
          step={inputType === 'number' ? step : undefined}
          value={defaultValue !== '' ? defaultValue : undefined} // Handle `value` conditionally
          onKeyDown={handleKeyDown}
          onWheel={handleWheel}
          data-testid={props['data-testid']}
          className={cn(
            inputClassName ?? '',
            highlightError
              ? 'bg-error-50 disabled:bg-error-50/50 read-only:bg-error-50/50'
              : '',
            inlineDimensionUnit ? 'pr-8' : '',
            inputEndAdornment ? 'pr-10' : ''
          )}
          {...register(name, {
            ...options,
            onChange: handleInputChange,
            required: required ? 'Required' : undefined, // Conditional required message
            maxLength:
              maxLength || thisFieldAttr.maxLength
                ? {
                    value: maxLength || thisFieldAttr.maxLength,
                    message: `Maximum length is ${maxLength || thisFieldAttr.maxLength} characters`,
                  }
                : undefined, // Conditional maxLength validation
          })}
        />

        {inlineDimensionUnit && (
          <Typography
            variant='body-xs'
            weight='medium'
            textColor='muted'
            className='absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none'
          >
            {inlineDimensionUnit}
          </Typography>
        )}

        {inputEndAdornment && (
          <div className='absolute right-1 top-1/2 flex -translate-y-1/2 items-center justify-center'>
            {inputEndAdornment}
          </div>
        )}
      </div>

      {prevValue ? <InputPreviousValue prevValue={prevValue} /> : null}
    </FormInputWrapper>
  );
}

export function FormatPhoneNumber(value: string): string {
  if (!value) return value;

  const phoneNumber = value.replace(/[^\d]/g, '').substring(0, 10);

  if (phoneNumber.length < 4) return phoneNumber;
  if (phoneNumber.length < 7) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  }
  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(
    3,
    6
  )}-${phoneNumber.slice(6)}`;
}

/**
 * Checks if the given RHF registration options contain the 'required' validation rule.
 *
 * @param options - The registration options object to verify.
 * @returns `true` if the 'required' rule exists; otherwise `false`.
 */
export function hasRequiredValidation(options?: RegisterOptions): boolean {
  return !!options?.required;
}

export function normalizeFieldAttributePath(name: string): string {
  return name
    .replace(/(?:\.(?:val|unit|value))+$/, '')
    .replace(/\.\d+(?=\.|$)/g, '');
}
